import { useState } from 'react';
import { Check } from 'lucide-react';
import { iconeDe } from './apparence';

const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

/**
 * Des contenus alternatifs, un seul visible à la fois.
 *
 * ========================================================================
 * POURQUOI CE BLOC EXISTE
 * ========================================================================
 *
 * La page d'installation décrit TROIS procédures incompatibles — iPhone,
 * Android, ordinateur. Les empiler les unes sous les autres obligerait
 * chaque lecteur à traverser deux méthodes qui ne le concernent pas, et la
 * plus probable serait qu'il suive la mauvaise.
 *
 * ========================================================================
 * CE QU'IL NE FAUT PAS FAIRE : DEVINER L'APPAREIL
 * ========================================================================
 *
 * Il serait tentant de présélectionner l'onglet d'après le navigateur. On ne
 * le fait pas : quelqu'un consulte souvent la page depuis son ordinateur
 * pour installer sur son téléphone, et une détection qui se trompe cache la
 * bonne réponse derrière un onglet que l'utilisateur ne pensera pas à
 * ouvrir. Le premier onglet est actif, les autres sont visibles — chacun
 * choisit.
 *
 * Structure attendue :
 *   { type: "onglets", titre?, onglets: [ { libelle, contenu?, etapes?[] } ] }
 *
 * `etapes` reprend la forme du bloc Etapes (titre, texte, astuce, image) :
 * même rendu, même vocabulaire, une seule chose à apprendre.
 */
const OngletsBloc = ({ bloc }) => {
    const onglets = Array.isArray(bloc?.onglets) ? bloc.onglets : [];
    const [actif, setActif] = useState(0);

    if (onglets.length === 0) return null;

    const courant = onglets[Math.min(actif, onglets.length - 1)] || {};
    const etapes = Array.isArray(courant.etapes) ? courant.etapes : [];

    return (
        <section className="mb-10" data-testid="bloc-onglets">
            {bloc.titre && (
                <h2 className="text-xl font-bold text-gray-900 mb-5">{bloc.titre}</h2>
            )}

            {/* LE SELECTEUR EST UNE CARTE, COMME LE RESTE DE LA PAGE.
                Il etait pose sur `bg-gray-100` : un creux gris, quand la page
                d'origine pose au contraire une barre BLANCHE a ombre douce,
                centree au-dessus de la carte des etapes. Sur un fond de page
                deja gris, le creux ne se distinguait plus du fond.

                role="tablist" : sans lui, un lecteur d'écran annonce une suite
                de boutons sans dire qu'ils s'excluent. */}
            <div className="flex justify-center mb-8">
                <div
                    role="tablist"
                    className="inline-flex flex-wrap gap-1 p-1 bg-white rounded-xl shadow-sm border border-gray-200"
                >
                    {onglets.map((o, i) => {
                        const IconeOnglet = iconeDe(o.icone);

                        return (
                            <button
                                key={i}
                                role="tab"
                                aria-selected={i === actif}
                                onClick={() => setActif(i)}
                                /* px-6 py-3 ET NON px-4 py-2 : la page d'origine
                                   donne a ses deux boutons une vraie surface de
                                   clic. Les reduire tassait la barre et la
                                   faisait passer pour un filtre secondaire. */
                                className={`flex items-center gap-2 px-6 py-3 font-medium rounded-lg transition-all ${
                                    i === actif
                                        ? 'bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white shadow-md'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                {IconeOnglet && <IconeOnglet className="w-5 h-5" />}
                                {o.libelle || `Onglet ${i + 1}`}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* UNE SEULE CARTE, DU TITRE AU MESSAGE DE REUSSITE.
                Le bandeau vert etait un bloc separe, avec son propre cadre et
                une gouttiere au-dessus : deux cartes qui se suivent au lieu
                d'une procedure qui se termine. Ici il fait partie du meme
                encadre, colle aux etapes — c'est la conclusion de la liste,
                pas un commentaire a cote. */}
            <div role="tabpanel" className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {(courant.titre || courant.sousTitre || courant.contenu) && (
                    <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                        {courant.titre && (
                            <h3 className="text-xl font-bold text-gray-900">{courant.titre}</h3>
                        )}
                        {courant.sousTitre && (
                            <p className="text-gray-600 mt-1">{courant.sousTitre}</p>
                        )}
                        {courant.contenu && (
                            <p className="text-gray-600 mt-2">{courant.contenu}</p>
                        )}
                    </div>
                )}

                {/* MEME DISPOSITION QUE LE BLOC ETAPES : numero dans la
                    pastille, icone dans son carre gris a cote du titre, une
                    carte par etape separee d'un trait. La page d'installation
                    d'origine presente ainsi ses quatre etapes, et deux rendus
                    differents pour la meme chose se remarquent. */}
                {etapes.length > 0 && (
                    <div className="divide-y divide-gray-100">
                        {etapes.map((e, i) => {
                            const Icone = iconeDe(e.icone);

                            return (
                                <div key={i} className="p-6 hover:bg-gray-50 transition-colors">
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cabaza-purple to-cabaza-pink flex items-center justify-center text-white font-bold">
                                                {i + 1}
                                            </div>
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start gap-3 mb-2">
                                                {Icone && (
                                                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                                        <Icone className="w-4 h-4 text-gray-600" />
                                                    </div>
                                                )}
                                                {e.titre && (
                                                    <h4 className="text-lg font-semibold text-gray-900 pt-1">
                                                        {e.titre}
                                                    </h4>
                                                )}
                                            </div>

                                            {e.texte && (
                                                <p className={`text-gray-600 ${Icone ? 'ml-11' : ''}`}>
                                                    {e.texte}
                                                </p>
                                            )}

                                            {e.astuce && (
                                                <div className={`mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg ${Icone ? 'ml-11' : ''}`}>
                                                    <p className="text-sm text-amber-800">{e.astuce}</p>
                                                </div>
                                            )}

                                            {/* Voir EtapesBloc : le cadre en
                                                pointilles annonce une capture a
                                                venir et garde la hauteur stable. */}
                                            {e.image ? (
                                                <img
                                                    src={e.image.startsWith('http') || e.image.startsWith('/')
                                                        ? e.image
                                                        : `${FILES_BASE_URL}/${e.image}`}
                                                    alt=""
                                                    loading="lazy"
                                                    className={`mt-4 rounded-xl border max-w-full h-auto ${Icone ? 'ml-11' : ''}`}
                                                />
                                            ) : e.emplacementImage ? (
                                                <div className={`mt-4 p-4 bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 ${Icone ? 'ml-11' : ''}`}>
                                                    <p className="text-sm text-gray-500 text-center">
                                                        {e.emplacementImage}
                                                    </p>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* LA CONCLUSION DE LA PROCEDURE, dans la meme carte.
                    Elle appartient a l'onglet : la marche a suivre sur iPhone
                    ne se termine pas comme celle sur ordinateur. --->
                    Son icone est ronde et verte, a gauche du texte — pas une
                    pastille numerotee : ce n'est pas une etape de plus, c'est
                    ce qu'on obtient quand on les a toutes faites. */}
                {(courant.succesTitre || courant.succesTexte) && (
                    <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-t border-green-100">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                <Check className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                                {courant.succesTitre && (
                                    <h4 className="font-semibold text-green-900">{courant.succesTitre}</h4>
                                )}
                                {courant.succesTexte && (
                                    <p className="text-green-700">{courant.succesTexte}</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default OngletsBloc;
